import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { timsurveyApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { StatoPraticaTimsurvey, TimsurveyListResponse, TimsurveyPratica } from '../../types/timsurvey'
import { ClipboardList } from 'lucide-react'

const RUOLI_BACKOFFICE = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico', 'readonly']

const STATI: { value: StatoPraticaTimsurvey | ''; label: string }[] = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'CREATA', label: 'Creata' },
  { value: 'ASSEGNATA', label: 'Assegnata' },
  { value: 'IN_COMPILAZIONE', label: 'In compilazione' },
  { value: 'COMPILATA', label: 'Compilata' },
  { value: 'FIRMATA', label: 'Firmata' },
  { value: 'CHIUSA', label: 'Chiusa' },
  { value: 'ANNULLATA', label: 'Annullata' },
]

const COLORI_STATO: Record<StatoPraticaTimsurvey, { bg: string; color: string }> = {
  CREATA:          { bg: 'rgba(100,116,139,.12)', color: '#64748b' },
  ASSEGNATA:       { bg: 'rgba(59,130,246,.12)',  color: '#2563eb' },
  IN_COMPILAZIONE: { bg: 'rgba(245,158,11,.12)',  color: '#d97706' },
  COMPILATA:       { bg: 'rgba(124,58,237,.12)',  color: '#7c3aed' },
  FIRMATA:         { bg: 'rgba(34,197,94,.12)',   color: '#15803d' },
  CHIUSA:          { bg: 'rgba(21,128,61,.15)',   color: '#166534' },
  ANNULLATA:       { bg: 'rgba(220,38,38,.12)',   color: '#dc2626' },
}

function BadgeStato({ stato }: { stato: StatoPraticaTimsurvey }) {
  const s = COLORI_STATO[stato]
  return (
    <span className="badge text-xs px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>
      {stato.replace(/_/g, ' ')}
    </span>
  )
}

function fmtData(d: string | null) {
  if (!d) return '—'
  try { return new Date(d).toLocaleString('it-IT') } catch { return d }
}

export default function TimsurveyPratiche() {
  const { user } = useAuth()
  const isBackoffice = !!user && RUOLI_BACKOFFICE.includes(user.role)

  const [stato, setStato] = useState<StatoPraticaTimsurvey | ''>('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 20

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onSearchChange = (value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { setSearch(value); setPage(1) }, 400)
  }

  const { data, isLoading } = useQuery({
    queryKey: ['timsurvey-pratiche', stato, search, page, isBackoffice],
    queryFn: () =>
      timsurveyApi.pratiche
        .list({ stato: stato || undefined, search: search || undefined, page, page_size: pageSize })
        .then(r => r.data as TimsurveyListResponse),
  })

  const pratiche = data?.items ?? []
  const totale = data?.totale ?? 0
  const totalPages = Math.max(1, Math.ceil(totale / pageSize))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Pratiche Sopralluogo</h1>
          <p className="page-subtitle">Modulo TIM Survey Cabling</p>
        </div>
        {isBackoffice && (
          <Link to="/timsurvey/nuova" className="btn-primary">+ Nuova pratica</Link>
        )}
      </div>

      {isBackoffice && (
        <div className="flex gap-3">
          <select className="select w-56" value={stato} onChange={e => { setStato(e.target.value as StatoPraticaTimsurvey | ''); setPage(1) }}>
            {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input
            className="input flex-1 max-w-sm"
            placeholder="Cerca per ragione sociale, ODA, GRS..."
            value={searchInput}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>
      )}

      {isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
      ) : pratiche.length === 0 ? (
        <div className="card p-12 text-center space-y-2">
          <ClipboardList size={40} className="mx-auto" style={{ color: '#cbd5e1' }} />
          <p className="text-slate-500 font-medium">Nessuna pratica trovata</p>
        </div>
      ) : isBackoffice ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="p-3">Codice</th>
                <th className="p-3">Ragione sociale</th>
                <th className="p-3">Città</th>
                <th className="p-3">Stato</th>
                <th className="p-3">Tecnico</th>
                <th className="p-3">Data sopralluogo</th>
              </tr>
            </thead>
            <tbody>
              {pratiche.map((p: TimsurveyPratica) => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="p-3">
                    <Link to={`/timsurvey/${p.id}`} className="font-medium" style={{ color: '#1E40AF' }}>{p.codice}</Link>
                  </td>
                  <td className="p-3">{p.ragione_sociale}</td>
                  <td className="p-3 text-slate-500">{p.citta ?? '—'}</td>
                  <td className="p-3"><BadgeStato stato={p.stato} /></td>
                  <td className="p-3 text-slate-500">{p.tecnico_id ?? '—'}</td>
                  <td className="p-3 text-slate-500">{fmtData(p.data_sopralluogo_prevista)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between p-3 text-sm text-slate-500">
            <span>{totale} pratiche — pagina {page}/{totalPages}</span>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Precedente</button>
              <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Successiva</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {pratiche.map((p: TimsurveyPratica) => (
            <div key={p.id} className="card p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800">{p.codice} — {p.ragione_sociale}</div>
                <div className="text-sm text-slate-500">{p.indirizzo ?? ''} {p.citta ?? ''}</div>
                <div className="mt-1"><BadgeStato stato={p.stato} /></div>
              </div>
              <Link to={`/timsurvey/${p.id}/wizard`} className="btn-primary whitespace-nowrap">Apri verbale</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
